import type { DeviceInfo, HubAlertSetupMode, SetupStep } from './hubAlertSetupRules';

/**
 * What Hub reports about each device's order-alert setup, so adoption can be followed per facility
 * (who is set up, who is stuck at which step, how sound tests go). Pure rules; see
 * setupTelemetry.ts for sending.
 */

export type SetupStateInput = {
  installId: string;
  erpUserId: number | null;
  facilityIds: ReadonlyArray<number>;
  selectedFacility: number | 'ALL' | null;
  device: DeviceInfo;
  browserVersion: string | null;
  installed: boolean;
  permission: NotificationPermission | 'unsupported';
  registration: string | null;
  step: SetupStep | null;
  applies: boolean;
  mode: HubAlertSetupMode;
  testPassedAt: string | null;
};

export const setupStateParams = (input: SetupStateInput) => ({
  install_id: input.installId,
  erp_user_id: input.erpUserId,
  facility_ids: [...input.facilityIds],
  selected_facility: input.selectedFacility,
  platform: input.device.platform,
  browser: input.device.browser,
  browser_version: input.browserVersion,
  installed: input.installed,
  permission: input.permission,
  registration: input.registration,
  // Devices setup does not cover (iPhone, other roles) are counted too, as not_covered.
  step: input.applies ? (input.step ?? 'ready') : 'not_covered',
  ready: input.applies && input.step === null,
  applies: input.applies,
  setup_mode: input.mode,
  test_passed_at: input.testPassedAt,
});

export type SetupStateParams = ReturnType<typeof setupStateParams>;

/** The parts whose change is worth a new event (not the browser version or timestamps). */
export const stateSignature = (params: SetupStateParams) =>
  JSON.stringify([
    params.erp_user_id,
    params.facility_ids,
    params.platform,
    params.browser,
    params.installed,
    params.permission,
    params.registration,
    params.step,
    params.applies,
    params.setup_mode,
    Boolean(params.test_passed_at),
  ]);

/** An unchanged device still reports this often, so the dashboard knows it is in use. */
export const STATE_HEARTBEAT_MS = 6 * 60 * 60 * 1000;

export type LastReport = { signature: string; at: number };

export const shouldReport = (signature: string, last: LastReport | null, now: number) =>
  !last || last.signature !== signature || now - last.at >= STATE_HEARTBEAT_MS;

export const parseLastReport = (raw: string | null): LastReport | null => {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<LastReport>;
    return typeof value.signature === 'string' && typeof value.at === 'number'
      ? { signature: value.signature, at: value.at }
      : null;
  } catch {
    return null;
  }
};

export type SetupTestResult = 'heard' | 'no_sound' | 'not_seen';

/**
 * Gateway's analytics endpoint sits beside the admin API, not under it: from
 * https://gateway.lyskraft.in/api/admin it is https://gateway.lyskraft.in/api/events/bulk.
 */
export const eventsUrl = (gatewayApiUrl: string | undefined) => {
  try {
    return `${new URL(gatewayApiUrl ?? '').origin}/api/events/bulk`;
  } catch {
    return null;
  }
};

/** `hub_alert_shown`: what an alert was, what was selected on screen, and how it was presented. */
export type AlertShownParams = {
  install_id: string;
  erp_user_id: number | null;
  alert_key: string;
  alert_type: string | null;
  alert_facility: number | null;
  priority: 'URGENT' | 'NORMAL';
  selected_facility: number | 'ALL' | null;
  /** alertPresentation: LOUD rings, SUBTLE chimes once (another facility), QUIET bell only. */
  presentation: 'LOUD' | 'SUBTLE' | 'QUIET';
  /** What this window did: rang, added it as new (bell), or left it to a ringing tab. */
  shown_as: 'ringing' | 'new';
  focused: boolean;
  ring_all: boolean;
};
